// Tapestry modal (§9.8, §14.2): the Golden Round postcard (1080² Canvas 2D PNG) with Save PNG and
// Copy result (the share text with one square per round placement). Opening it also keeps the
// card as the Bean Card's "last postcard".
import { NO_SLOT } from '@shared/constants';
import type { LiveScreen } from '../contracts';
import type { UiDeps } from '../deps';
import { beanIcon, icon } from '../icons';
import { AWARD_NAMES, ordinal } from '../labels';
import { copyText, el, setHidden, setText } from '../ui';
import { button, closeButton, setLabel } from '../widgets';

const SQUARES = ['🟨', '🔲', '🟫', '⬜'] as const;

/** The §9.8 share text: `SHOVE OFF! [KDFT] — I placed 2nd (17 pts) • 6 KOs • Banana Baron` + squares. */
export function shareText(deps: UiDeps): string {
  const room = deps.room;
  const end = room.matchEnd;
  const me = room.slot;
  const st = end?.standings.find((s) => s.slot === me);
  const kos = end?.stats[me]?.kos ?? 0;
  const award = end?.awards.find((a) => a.slot === me);
  const parts = [`SHOVE OFF! [${room.code}] — ${st ? `I placed ${ordinal(st.place)} (${st.score} pts)` : 'I watched'}`, `${kos} KO${kos === 1 ? '' : 's'}`];
  if (award) parts.push(titleCase(AWARD_NAMES[award.id]));
  const squares = room.roundEnds.map((r) => {
    const res = r.results.find((x) => x.slot === me);
    const place = res?.place ?? 0;
    return place >= 1 && place <= 3 ? SQUARES[place - 1] : SQUARES[3];
  }).join('');
  return `${parts.join(' • ')}${squares ? `\n${squares}` : ''}`;
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function create(deps: UiDeps): LiveScreen {
  const img = el('img', { class: 'tap-img', attrs: { alt: 'Tapestry postcard of the Golden Round', draggable: 'false' } });
  const empty = el('div', { class: 'tap-empty' }, beanIcon('#9B6BFF', 'dizzy', 'ico'), el('p', { text: 'No postcard yet — finish a match to get one!' }));
  const share = el('pre', { class: 'tap-share' });
  const save = el('a', { class: 'btn pink', attrs: { download: 'shoveoff-postcard.png', role: 'button', tabindex: 0 } }, icon('download'), el('span', { class: 'lbl', text: 'SAVE PNG' }));
  const copy = button({ label: 'COPY RESULT', icon: 'copy', variant: 'sky', onClick: async () => {
    const ok = await copyText(shareText(deps));
    setLabel(copy, ok ? 'COPIED!' : 'COPY FAILED');
    deps.services.playUi(ok ? 'coin' : 'error');
  } });
  let savedFor = '';

  return {
    el: el('div', { class: 'screen modal' },
      el('div', { class: 'panel sheet with-ribbon tapestry-panel springy' },
        el('div', { class: 'ribbon gold', text: 'TAPESTRY POSTCARD' }),
        closeButton(() => deps.nav.back()),
        el('div', { class: 'sheet-body tap-body' }, el('div', { class: 'tap-frame' }, img, empty), share),
        el('div', { class: 'sheet-foot row center wrap' },
          button({ label: 'BACK', icon: 'back', variant: 'cream', onClick: () => deps.nav.back() }), copy, save))),
    onShow() {
      setLabel(copy, 'COPY RESULT');
      setText(share, shareText(deps));
      const rounds = deps.services.tapestry.rounds();
      const last = rounds.length > 0 ? rounds[rounds.length - 1] : -1;
      const room = deps.room;
      const end = room.matchEnd;
      const players = room.dto?.players ?? [];
      const standings = (end?.standings ?? []).slice().sort((a, b) => a.place - b.place).map((s) => {
        const p = players[s.slot];
        return { name: p?.name ?? `Bean ${s.slot + 1}`, score: s.score, identity: p?.identity ?? s.slot };
      });
      let canvas: HTMLCanvasElement | null = null;
      if (last >= 0) {
        try {
          canvas = deps.services.tapestry.render(last, { code: room.code, date: new Date(), standings, colorblind: deps.settings().accessibility.colorblind });
        } catch {
          canvas = null;
        }
      }
      const url = canvas ? canvas.toDataURL('image/png') : deps.profile().lastPostcard;
      setHidden(img, !url);
      setHidden(empty, !!url);
      setHidden(save, !url);
      if (url) {
        img.src = url;
        save.setAttribute('href', url);
        save.setAttribute('download', `shoveoff-${room.code || 'postcard'}.png`);
      }
      const key = `${room.code}:${last}`;
      if (canvas && url && savedFor !== key && room.slot !== NO_SLOT) {
        savedFor = key;
        deps.services.profiles.setPostcard(url);
      }
    },
  };
}
